import React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import ContainedButton from '../../../components/base/ContainedButton';
import TextButton from '../../../components/base/TextButton';
import { useMenu } from '../../../components/base/useMenu';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@emotion/react';
import Typography from '@mui/material/Typography';

const Reports = ({
  handleDownloadReport,
  isLoading,
  setReportFormat,
  format,
  mode,
  setMode,
  suite,
  setSource,
  source,
  runs
}) => {
  const { menuProps, triggerProps } = useMenu();
  const theme = useTheme();

  const hasRuns = runs?.length > 0;

  return (
    <Box>
      <TextButton
        {...triggerProps}
        loadingProps={{ size: 16 }}
        disabled={!hasRuns}
        startIcon={
          <CloudDownloadIcon
            sx={{
              color: hasRuns ? theme.palette.primary.main : theme.palette.action.disabled
            }}
          />
        }
        isLoading={isLoading}
        sx={{
          position: 'relative',
          '&:hover': {
            backgroundColor: theme.palette.action.hover
          },
          '&.Mui-disabled': {
            backgroundColor: 'transparent'
          }
        }}>
        Export
      </TextButton>

      <Popover
        {...menuProps}
        elevation={2}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            minWidth: 320,
            maxWidth: 400,
            overflow: 'visible',
            backgroundImage: 'none',
            backgroundColor: theme.palette.background.paper
          }
        }}>
        <Box
          sx={{
            px: 3,
            py: 2.5,
            position: 'relative',
            zIndex: 1,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <CloudDownloadIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
              Export Reports
            </Typography>
          </Box>

          <Divider
            sx={{
              mb: 2.5,
              borderColor: theme.palette.divider
            }}
          />

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  mb: 1.5,
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  '&.Mui-focused': {
                    color: theme.palette.primary.main
                  }
                }}>
                Report Mode
              </FormLabel>
              <RadioGroup
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                row
                sx={{
                  gap: 2,
                  '& .MuiFormControlLabel-root': {
                    margin: 0
                  }
                }}>
                <FormControlLabel
                  value="simple"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        '&.Mui-checked': {
                          color: theme.palette.primary.main
                        },
                        '&.Mui-disabled': {
                          color: theme.palette.action.disabled
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" fontWeight={500} color="text.primary">
                        Simple
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Basic report format
                      </Typography>
                    </Box>
                  }
                  sx={{
                    flex: 1,
                    border: `1px solid ${
                      mode === 'simple' ? theme.palette.primary.main : theme.palette.divider
                    }`,
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    backgroundColor:
                      mode === 'simple'
                        ? theme.palette.mode === 'dark'
                          ? 'rgba(144, 202, 249, 0.08)'
                          : 'rgba(33, 150, 243, 0.04)'
                        : 'transparent',
                    '&:hover': {
                      borderColor: theme.palette.primary.light,
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(144, 202, 249, 0.12)'
                          : 'rgba(33, 150, 243, 0.08)'
                    }
                  }}
                />
                <FormControlLabel
                  value="advanced"
                  disabled
                  control={
                    <Radio
                      size="small"
                      sx={{
                        '&.Mui-checked': {
                          color: theme.palette.primary.main
                        },
                        '&.Mui-disabled': {
                          color: theme.palette.action.disabled
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" fontWeight={500} color="text.disabled">
                        Advanced
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Coming soon
                      </Typography>
                    </Box>
                  }
                  sx={{
                    flex: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    opacity: 0.6,
                    '&:hover': {
                      cursor: 'not-allowed'
                    }
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  mb: 1.5,
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  '&.Mui-focused': {
                    color: theme.palette.primary.main
                  }
                }}>
                File Format
              </FormLabel>
              <RadioGroup
                value={format}
                onChange={(e) => setReportFormat(e.target.value)}
                row
                sx={{
                  gap: 1.5,
                  '& .MuiFormControlLabel-root': {
                    margin: 0
                  }
                }}>
                {[
                  { value: 'excel', label: 'Excel', available: true },
                  { value: 'csv', label: 'CSV', available: true },
                  { value: 'pdf', label: 'PDF', available: false }
                ].map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    disabled={!option.available}
                    control={
                      <Radio
                        size="small"
                        sx={{
                          '&.Mui-checked': {
                            color: theme.palette.primary.main
                          },
                          '&.Mui-disabled': {
                            color: theme.palette.action.disabled
                          }
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color={option.available ? 'text.primary' : 'text.disabled'}>
                        {option.label}
                      </Typography>
                    }
                    sx={{
                      flex: 1,
                      border: `1px solid ${
                        format === option.value ? theme.palette.primary.main : theme.palette.divider
                      }`,
                      borderRadius: 1,
                      px: 2,
                      py: 1.5,
                      backgroundColor:
                        format === option.value
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(144, 202, 249, 0.08)'
                            : 'rgba(33, 150, 243, 0.04)'
                          : 'transparent',
                      '&:hover': option.available
                        ? {
                            borderColor: theme.palette.primary.light,
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(144, 202, 249, 0.12)'
                                : 'rgba(33, 150, 243, 0.08)'
                          }
                        : {},
                      opacity: option.available ? 1 : 0.6,
                      '&.Mui-disabled': {
                        opacity: 0.5,
                        '&:hover': {
                          borderColor: theme.palette.divider,
                          backgroundColor: 'transparent'
                        }
                      }
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          <Box
            sx={{
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(2, 136, 209, 0.16)'
                  : 'rgba(144, 202, 249, 0.16)',
              border: `1px solid ${
                theme.palette.mode === 'dark'
                  ? 'rgba(144, 202, 249, 0.3)'
                  : 'rgba(33, 150, 243, 0.2)'
              }`,
              borderRadius: 1,
              p: 1.5,
              mb: 2.5,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}>
            <InfoOutlinedIcon
              fontSize="small"
              sx={{
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.info.light
                    : theme.palette.info.main,
                mt: 0.25
              }}
            />
            <Typography
              variant="caption"
              sx={{
                lineHeight: 1.4,
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary'
              }}>
              <Box component="span" fontWeight={600}>
                Note:
              </Box>{' '}
              You can only download results from the last 3 months. If you need older reports,
              please use the date filter to select a specific time period.
            </Typography>
          </Box>

          <ContainedButton
            fullWidth
            size="large"
            onClick={() => {
              handleDownloadReport();
              menuProps.onClose();
            }}
            startIcon={<CloudDownloadIcon />}
            disabled={!hasRuns}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 600,
              py: 1.25,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                    : '0 4px 12px rgba(0, 0, 0, 0.15)'
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
                boxShadow: 'none'
              }
            }}>
            Download Report
          </ContainedButton>
        </Box>
      </Popover>
    </Box>
  );
};

export default Reports;
